import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { formatMoney } from './money-formatting.js';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement} priceDisplay - The main price display container (Desktop)
 * @property {HTMLElement} buttonPrice - The price display inside the button (Mobile)
 * @property {any} quantitySelector - The quantity selector component
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener('change', this.#handleVariantIdChange, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    // console.log('StickyAddToCart: Setting up observers');
    
    // Defer execution slightly to ensure DOM components are upgraded
    setTimeout(() => {
      const productForm = this.#getProductForm();
      if (!productForm) {
        // console.error('StickyAddToCart: Product form not found for product ID:', this.dataset.productId);
        return;
      }

      const buyButtonsBlock = productForm.closest('.buy-buttons-block');
      // if (!buyButtonsBlock) {
      //   // console.error('StickyAddToCart: .buy-buttons-block not found as parent of', productForm);
      //   return;
      // }
      // console.log('StickyAddToCart: Found buyButtonsBlock', buyButtonsBlock);

      // In themes migrated from 2.0, the footer element doesn't exist
      const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
      if (!footer) {
        console.warn('StickyAddToCart: Footer not found, sticky bar might stay visible at bottom');
      }

      // Observer for buy buttons visibility
      this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;

        // console.log('StickyAddToCart: BuyButtons entry', { 
        //   isIntersecting: entry.isIntersecting, 
        //   isStuck: this.#isStuck,
        //   rect: entry.target.getBoundingClientRect()
        // });

        // Only show sticky bar if buy buttons have been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          // Check if the element is above the viewport (scrolled past) or below (not yet reached)
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            // console.log('StickyAddToCart: Showing bar (scrolled past)');
            this.#showStickyBar();
          }
          // If rect.top >= 0, element is below viewport - don't show sticky bar yet
        } else if (entry.isIntersecting && this.#isStuck) {
          // console.log('StickyAddToCart: Hiding bar (intersecting)');
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      });

      // Observer for footer visibility - hides sticky bar at page bottom
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            // Footer out of view - check if we should show sticky bar again
            const rect = buyButtonsBlock.getBoundingClientRect();
            // Only show if buy buttons are above the viewport (scrolled past)
            if (rect.bottom < 0 || rect.top < 0) {
              this.#hiddenByBottom = false;
              this.#showStickyBar();
            }
          }
        },
        {
          rootMargin: '200px 0px 0px 0px',
        }
      );

      this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
      if (footer) this.#mainBottomObserver.observe(footer);
      this.#targetAddToCartButton = this.#findAddToCartButton(productForm);
    }, 100);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // The gift card CTA stays disabled until its recipient fields are filled:
    // bring the form into view and let the browser flag what is missing.
    if (this.#targetAddToCartButton.disabled) {
      const form = this.#getProductForm()?.querySelector('form');
      form?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      form?.reportValidity();
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // Fly-to-cart animation is optional (requires both cartIcon and productImage)
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (cartIcon && this.refs.productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
    }

    // Yield to main thread so getAnimations() captures the correct animations
    await yieldToMainThread();
    await onAnimationEnd(this.refs.addToCartButton);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = this.#findAddToCartButton(productForm);
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Follows variant changes written straight to the form's hidden `id` input.
   * The gift card form resolves its amount + style pair client side, without
   * the variant-update re-render, so the bar repaints from the section's
   * variant JSON instead.
   * @param {Event} event - The change event
   */
  #handleVariantIdChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'id') return;
    if (input.closest('product-form-component') !== this.#getProductForm()) return;

    const variant = this.#getVariantData(input.value);
    if (!variant) return;

    this.dataset.currentVariantId = String(variant.id);

    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    if (variantTitleElement && variant.title) variantTitleElement.textContent = variant.title;

    const { priceDisplay } = this.refs;
    if (priceDisplay) priceDisplay.dataset.basePrice = String(variant.price);

    this.#updateButtonText();
  };

  /**
   * Looks a variant up in the section's `CustomPdpVariantPrices` JSON.
   * @param {string} variantId
   * @returns {{ id: string | number, title?: string, price: number } | null}
   */
  #getVariantData(variantId) {
    const sectionId = /** @type {HTMLElement | null} */ (this.closest('[data-section-id]'))?.dataset.sectionId;
    const script = sectionId ? document.getElementById(`CustomPdpVariantPrices-${sectionId}`) : null;
    if (!script) return null;

    try {
      const variants = /** @type {Array<{ id: string | number, title?: string, price: number }>} */ (
        JSON.parse(script.textContent || '[]')
      );
      return variants.find((variant) => String(variant.id) === variantId) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    // Ignore sync events from other sources (prevent loops)
    if (event.detail.source === 'sync') return;

    this.#currentQuantity = event.detail.quantity;

    // Sync with internal quantity selector component
    const { quantitySelector } = this.refs;
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(this.#currentQuantity.toString());
      quantitySelector.updateButtonStates();
    }

    // Sync with product-form quantity selector
    const productForm = this.#getProductForm();
    if (productForm) {
      const formComponent = /** @type {any} */ (productForm);
      const formQuantitySelector = formComponent?.refs?.quantitySelector;
      if (formQuantitySelector?.setValue) {
        formQuantitySelector.setValue(this.#currentQuantity.toString());
        formQuantitySelector.updateButtonStates();
      }

      // Dispatch event on product-form with source='sync' so it knows not to re-broadcast
      productForm.dispatchEvent(new QuantitySelectorUpdateEvent(this.#currentQuantity, undefined, 'sync'));
    }

    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    // console.log('StickyAddToCart: _showStickyBar called');
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.style.visibility = 'visible';
    this.style.display = 'block';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    // console.log('StickyAddToCart: _hideStickyBar called');
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.style.visibility = 'hidden';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    // First try within the same section (standard case)
    const sectionElement = this.closest('.shopify-section') || this.closest('.c-section-custom-pdp');
    if (sectionElement) {
      const form = sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
      if (form) return form;
    }

    // Fallback to searching globally if section lookup fails
    return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * Gets the add to cart button that submits the product form. The gift card
   * template renders it in a separate block, outside `product-form-component`,
   * bound to the form through its `form` attribute.
   * @param {HTMLElement} productForm
   * @returns {HTMLButtonElement | null}
   */
  #findAddToCartButton(productForm) {
    const inner = productForm.querySelector('[ref="addToCartButton"]');
    if (inner instanceof HTMLButtonElement) return inner;

    // `form.id` is shadowed by the hidden `name="id"` input, so read the attribute.
    const formId = productForm.querySelector('form')?.getAttribute('id');
    if (!formId) return null;

    const external = document.querySelector(`button[type="submit"][form="${formId}"]`);
    return external instanceof HTMLButtonElement ? external : null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber, priceDisplay, buttonPrice } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    if (quantityNumber) {
      quantityNumber.textContent = this.#currentQuantity.toString();
    }

    // Show/hide the quantity display based on availability and quantity
    if (quantityDisplay) {
      if (available && this.#currentQuantity > 1) {
        quantityDisplay.style.display = 'inline';
      } else {
        quantityDisplay.style.display = 'none';
      }
    }

    // Update prices based on quantity
    if (priceDisplay) {
      const basePrice = parseInt(priceDisplay.dataset.basePrice || '0') || 0;
      const totalPrice = basePrice * this.#currentQuantity;
      const themeSettings = /** @type {any} */ (window).theme || {};
      
      const formattedPrice = formatMoney(
        totalPrice,
        themeSettings.moneyFormat || '${{amount}}',
        themeSettings.currencyCode || 'USD'
      );

      const formattedPriceWithCurrency = formatMoney(
        totalPrice,
        themeSettings.moneyWithCurrencyFormat || '${{amount}} {{currency}}',
        themeSettings.currencyCode || 'USD'
      );

      // Update main price (Desktop)
      // `.price` is what the `price` snippet renders; the other two are legacy markup.
      const priceItems = priceDisplay.querySelectorAll('.price, .price-item--regular, .price-item--sale');
      if (priceItems.length > 0) {
        priceItems.forEach((item) => {
          item.textContent = formattedPrice;
        });
      }

      // Update button price (Mobile/Button)
      if (buttonPrice) {
        buttonPrice.textContent = formattedPriceWithCurrency;
      }
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
